import React, {useLayoutEffect, useEffect} from 'react';
import {Provider} from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import AuthRoutes from '@crema/utility/AuthRoutes';
import AppContextProvider from '@crema/utility/AppContextProvider';
import AppThemeProvider from '@crema/utility/AppThemeProvider';
import AppStyleProvider from '@crema/utility/AppStyleProvider';
import AppLocaleProvider from '@crema/utility/AppLocaleProvider';
import AppLayout from '@crema/core/AppLayout';
import configureStore from 'redux/store';
import ReactQueryProvider from '@crema/utility/ReactQueryProvider';
import {DynamicRoutesProvider} from './contexts/DynamicRoutesContext';

// Import global skeleton styles
import './styles/skeleton.css';

import {BrowserRouter, useNavigate} from 'react-router-dom';
import JWTAuthAuthProvider from '@crema/services/auth/jwt-auth/JWTAuthProvider';
import {LayoutDirection} from './shared/constants/AppEnums';
import DynamicMetaTags from '@crema/utility/DynamicMetaTags';

const DIR_KEY = 'app_direction';
const store = configureStore();

// Sets <html dir> ASAP (before MUI renders) to prevent LTR→RTL flash
const BootDirection = () => {
  useLayoutEffect(() => {
    const saved =
      typeof window !== 'undefined' ? localStorage.getItem(DIR_KEY) : null;
    const dir = saved === LayoutDirection.RTL ? 'rtl' : 'ltr';
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', dir);
    }
  }, []);
  return null;
};

// Listens for unauthorized errors and redirects safely
const GlobalAuthListener = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleUnauthorized = () => {
      // Small timeout to coordinate state cleanly
      setTimeout(() => {
        if (window.location.pathname !== '/signin') {
          navigate('/signin', {replace: true});
        }
      }, 100);
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, [navigate]);

  return null;
};

const App = () => (
  <AppContextProvider>
    <Provider store={store}>
      <ReactQueryProvider>
        {/* Locale FIRST so Theme can initialize with correct direction */}
        <AppLocaleProvider>
          <AppThemeProvider>
            <AppStyleProvider>
              <BrowserRouter>
                <JWTAuthAuthProvider>
                  <DynamicRoutesProvider>
                    <AuthRoutes>
                      <BootDirection />
                      <GlobalAuthListener />
                      <DynamicMetaTags />
                      <CssBaseline />
                      <AppLayout />
                    </AuthRoutes>
                  </DynamicRoutesProvider>
                </JWTAuthAuthProvider>
              </BrowserRouter>
            </AppStyleProvider>
          </AppThemeProvider>
        </AppLocaleProvider>
      </ReactQueryProvider>
    </Provider>
  </AppContextProvider>
);

export default App;
